"use client";

import { usePathname } from "next/navigation";
import { useAuth } from "@/hooks/use-auth";
import { Header as V2Header } from "@/v2/layouts/Header";
import { Footer as V2Footer } from "@/v2/layouts/Footer";
import { isPublicInformationalRoute } from "@/lib/auth/route-guards";

/**
 * Layout chrome resolver.
 *
 *   • /admin/login            → bare main, no chrome (custom admin layout).
 *   • Marketing routes        → V2 header + footer, ALWAYS (regardless of auth state). This
 *                               includes the home page and every public informational page
 *                               (about, contact, how-it-works, faq, terms, privacy, etc.).
 *   • Authenticated app routes → no marketing chrome; the app's own layouts supply the UI
 *                                (dashboard sidebar, bookings shell, etc.).
 *
 * The previous version stripped the marketing chrome the moment a session was detected,
 * which left signed-in users on the home page with no header/footer. Marketing chrome is
 * now bound to the ROUTE, not the auth state.
 */
export default function LayoutShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() ?? "";
  const { isAuthenticated, loading } = useAuth();
  const isAdminLogin = pathname === "/admin/login";
  /** Root URL + every PUBLIC_INFORMATIONAL_ROUTE share the marketing chrome. */
  const usesMarketingChrome = pathname === "/" || isPublicInformationalRoute(pathname);

  if (isAdminLogin) {
    return <main className="flex min-h-0 flex-1 flex-col">{children}</main>;
  }

  if (usesMarketingChrome) {
    return (
      <div className="flex min-h-screen flex-col font-sans">
        <V2Header />
        <main className="flex min-h-0 flex-1 flex-col bg-v2-neutral-0 pt-[72px] sm:pt-[88px] lg:pt-[108px]">{children}</main>
        <V2Footer />
      </div>
    );
  }

  /** Logged-in (or auth still resolving) on a non-marketing route: app layouts own the UI. */
  if (loading || isAuthenticated) {
    return (
      <main className="flex min-h-0 min-h-screen w-full flex-1 flex-col bg-gray-50">{children}</main>
    );
  }

  /** Fallback for any remaining route that wasn't classified — render marketing chrome so
   *  guests at least get header + footer rather than a bare page. */
  return (
    <div className="flex min-h-screen flex-col font-sans">
      <V2Header />
      <main className="flex min-h-0 flex-1 flex-col bg-v2-neutral-0 pt-[108px]">{children}</main>
      <V2Footer />
    </div>
  );
}
